<template>
  <header class="border-b border-gray-200 bg-white text-gray-900">
    <div class="container mx-auto px-2 sm:px-4 lg:px-6 max-w-full">
      <div class="flex h-16 items-center justify-between">
        <!-- Brand name and navigation -->
        <div class="flex items-center space-x-4">
          <!-- Brand name -->
          <div>
            <router-link to="/" class="text-lg font-bold text-gray-900 whitespace-nowrap">
              Racing Dashboard
            </router-link>
          </div>

          <!-- Desktop navigation -->
          <nav class="hidden md:flex space-x-4">
            <router-link 
              v-if="!$route.path.includes('/login') && !$route.path.includes('/register')" 
              to="/" 
              class="border-b-2 px-1 py-1 text-sm font-medium transition-colors hover:border-gray-900"
              :class="{ 'border-gray-900': $route.path === '/', 'border-transparent': $route.path !== '/' }"
            >
              Overview
            </router-link>
            
            <router-link 
              v-if="isAuthenticated" 
              to="/search" 
              class="border-b-2 px-1 py-1 text-sm font-medium transition-colors hover:border-gray-900"
              :class="{ 'border-gray-900': $route.path === '/search', 'border-transparent': $route.path !== '/search' }"
            >
              Query Tool
            </router-link>

            <router-link 
              v-if="isAuthenticated" 
              to="/systems" 
              class="border-b-2 px-1 py-1 text-sm font-medium transition-colors hover:border-gray-900"
              :class="{ 'border-gray-900': $route.path.startsWith('/systems'), 'border-transparent': !$route.path.startsWith('/systems') }"
            >
              My Systems
            </router-link>

            <router-link 
              v-if="isAuthenticated" 
              to="/analytics" 
              class="border-b-2 px-1 py-1 text-sm font-medium transition-colors hover:border-gray-900"
              :class="{ 'border-gray-900': $route.path.startsWith('/analytics'), 'border-transparent': !$route.path.startsWith('/analytics') }"
            >
              Analytics
            </router-link>

            <router-link 
              v-if="isAuthenticated" 
              to="/reports" 
              class="border-b-2 px-1 py-1 text-sm font-medium transition-colors hover:border-gray-900"
              :class="{ 'border-gray-900': $route.path.startsWith('/reports'), 'border-transparent': !$route.path.startsWith('/reports') }"
            >
              Reports
            </router-link>
          </nav>
        </div>
        
        <!-- User menu -->
        <div v-if="!$route.path.includes('/login') && !$route.path.includes('/register')" class="flex items-center space-x-3 ml-auto">
          <template v-if="!isAuthenticated">
            <router-link to="/login">
              <UiButton variant="outline" size="sm">
                Login
              </UiButton>
            </router-link>
          </template>
          
          <div v-else class="flex items-center space-x-3">
            <!-- User avatar -->
            <div class="flex items-center space-x-2">
              <div class="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium">
                {{ currentUser.email ? currentUser.email.charAt(0).toUpperCase() : 'U' }}
              </div>
              <span class="text-sm font-medium text-gray-600">{{ currentUser.email ? currentUser.email.split('@')[0] : 'User' }}</span>
            </div>

            <!-- Sign out button -->
            <UiButton 
              @click="handleLogout" 
              variant="default"
              size="sm"
              class="bg-gray-900 hover:bg-gray-800"
            >
              Sign Out
            </UiButton>
          </div>
        </div>
        
        <!-- Mobile menu button -->
        <div class="md:hidden">
          <UiButton 
            @click="isMobileMenuOpen = !isMobileMenuOpen"
            variant="ghost"
            size="icon"
            class="text-gray-900 hover:text-gray-700"
          >
            <svg v-if="!isMobileMenuOpen" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="4" x2="20" y1="12" y2="12"/>
              <line x1="4" x2="20" y1="6" y2="6"/>
              <line x1="4" x2="20" y1="18" y2="18"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 6 6 18"/>
              <path d="m6 6 12 12"/>
            </svg>
          </UiButton>
        </div>
      </div>
    </div>
    
    <!-- Mobile menu -->
    <div v-if="isMobileMenuOpen" class="md:hidden border-t border-gray-200 py-4 px-4">
      <div class="space-y-3 flex flex-col">
        <router-link 
          to="/" 
          class="text-sm font-medium block py-2 border-l-2 pl-2 hover:text-gray-700"
          :class="{ 'border-gray-900 font-bold': $route.path === '/', 'border-transparent': $route.path !== '/' }"
          @click="isMobileMenuOpen = false"
        >
          Overview
        </router-link>
        
        <router-link 
          v-if="isAuthenticated" 
          to="/search" 
          class="text-sm font-medium block py-2 border-l-2 pl-2 hover:text-gray-700"
          :class="{ 'border-gray-900 font-bold': $route.path === '/search', 'border-transparent': $route.path !== '/search' }"
          @click="isMobileMenuOpen = false"
        >
          Query Tool
        </router-link>

        <router-link 
          v-if="isAuthenticated" 
          to="/systems" 
          class="text-sm font-medium block py-2 border-l-2 pl-2 hover:text-gray-700"
          :class="{ 'border-gray-900 font-bold': $route.path.startsWith('/systems'), 'border-transparent': !$route.path.startsWith('/systems') }"
          @click="isMobileMenuOpen = false"
        >
          My Systems
        </router-link>

        <router-link 
          v-if="isAuthenticated" 
          to="/analytics" 
          class="text-sm font-medium block py-2 border-l-2 pl-2 hover:text-gray-700"
          :class="{ 'border-gray-900 font-bold': $route.path.startsWith('/analytics'), 'border-transparent': !$route.path.startsWith('/analytics') }"
          @click="isMobileMenuOpen = false"
        >
          Analytics
        </router-link>

        <router-link 
          v-if="isAuthenticated" 
          to="/reports" 
          class="text-sm font-medium block py-2 border-l-2 pl-2 hover:text-gray-700"
          :class="{ 'border-gray-900 font-bold': $route.path.startsWith('/reports'), 'border-transparent': !$route.path.startsWith('/reports') }"
          @click="isMobileMenuOpen = false"
        >
          Reports
        </router-link>
      </div>
    </div>
  </header>
</template>

<script>
import { ref } from 'vue';
import { mapGetters, mapActions } from 'vuex';
import UiButton from '@/components/ui/button.vue';

export default {
  name: 'AppNavbar',
  components: {
    UiButton
  },

  setup() {
    const isUserMenuOpen = ref(false);
    const isMobileMenuOpen = ref(false);
    
    return {
      isUserMenuOpen,
      isMobileMenuOpen
    };
  },
  computed: {
    ...mapGetters(['isAuthenticated', 'currentUser'])
  },
  methods: {
    ...mapActions(['logout']),
    handleLogout() {

      this.isUserMenuOpen = false;
      this.isMobileMenuOpen = false;
      this.logout();
      this.$router.push('/login');
    }
  }
}
</script>
